import { CloseIcon, IconSizeEnum } from '@acceso/design-system';
import { useEffect, useRef, useState, useCallback, RefObject, MouseEvent } from 'react';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { BoxRow } from '@/features/broadcast-schedule-program/types';
import { getVideoFrame } from '@/helpers/video-frame';

export type VideoBoxesProps = {
  activeBox: BoxRow | null;
  containerRef: RefObject<HTMLDivElement>;
  videoRef?: RefObject<HTMLVideoElement | null>;
};

export function VideoBoxes({ activeBox, containerRef, videoRef }: VideoBoxesProps) {
  const { setVisibleBox } = useBroadcastScheduleProgram();
  const boxRef = useRef<HTMLDivElement>(null);
  const [shouldReverseControls, setShouldReverseControls] = useState(false);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const updateContainerSize = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    }
  }, [containerRef]);

  useEffect(() => {
    if (boxRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const boxRect = boxRef.current.getBoundingClientRect();

      const rightDistance = containerRect.right - boxRect.right;
      setShouldReverseControls(rightDistance < 120);
    }
    updateContainerSize();
  }, [activeBox, containerRef, updateContainerSize]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new window.ResizeObserver(() => {
      updateContainerSize();
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, updateContainerSize]);

  if (!activeBox) {
    return null;
  }

  const handleRemoveActiveBox = (event: MouseEvent) => {
    event.stopPropagation();
    setVisibleBox(null);
  };

  const { bbox, entity_label, entity_score } = activeBox;
  const [left, top, right, bottom] = bbox;
  const width = right - left;
  const height = bottom - top;

  const frame =
    videoRef?.current && containerRef.current
      ? getVideoFrame(containerRef.current, videoRef.current)
      : null;

  const baseWidth = frame ? frame.width : containerSize.width;
  const baseHeight = frame ? frame.height : containerSize.height;
  const offsetLeft = frame?.left ?? 0;
  const offsetTop = frame?.top ?? 0;
  const computedStyle = {
    left: baseWidth ? Math.floor(offsetLeft + left * baseWidth) : `${left * 100}%`,
    top: baseHeight ? Math.floor(offsetTop + top * baseHeight) : `${top * 100}%`,
    width: baseWidth ? Math.floor(width * baseWidth) : `${width * 100}%`,
    height: baseHeight ? Math.floor(height * baseHeight) : `${height * 100}%`,
  };

  return (
    <div data-id="videobox-layer" ref={boxRef}>
      <div
        className="absolute border-2 border-black drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
        style={computedStyle}
      >
        <svg className="pointer-events-none absolute left-[-2px] top-[-2px] h-[calc(100%+4px)] w-[calc(100%+4px)] drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]">
          <rect
            x="0"
            y="0"
            width={computedStyle.width as string}
            height={computedStyle.height as string}
            fill="none"
            stroke="#ffffff"
            strokeWidth="4"
            strokeDasharray="6 4"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="-10"
              dur="0.5s"
              repeatCount="indefinite"
            />
          </rect>
        </svg>
      </div>
      <div
        className="absolute z-10 drop-shadow-[0_0_3px_rgba(255,255,255,0.8)]"
        style={computedStyle}
      >
        <button
          onClick={handleRemoveActiveBox}
          aria-label="Cerrar"
          className={`absolute z-10 flex h-6 w-6 -translate-y-1/2 transform items-center justify-center rounded-full bg-white text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)] ${shouldReverseControls ? '-top-[12px] left-0 -translate-x-1/2' : '-right-[6px] -top-[2px] translate-x-1/2'}`}
        >
          <CloseIcon size={IconSizeEnum.SM} className="text-black" />
        </button>

        {entity_label && (
          <span
            className={`absolute -top-[8px] whitespace-nowrap rounded bg-white px-2 py-1 text-xs font-bold text-black ${shouldReverseControls ? 'left-0 -translate-x-full transform' : 'right-0 translate-x-full transform'}`}
          >
            {entity_label} {entity_score && <span>&nbsp;{(entity_score * 100).toFixed(0)}%</span>}
          </span>
        )}
      </div>
    </div>
  );
}
